import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Modal,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import CustomRankingInput from "../../components/formComponent/CustomRankingInput";
import CustomSelect from "../../components/formComponent/CustomSelect";
import { addObservation,  updateObservation } from "../../services/observationsApi";
import { useState } from "react";
import { toast } from "react-toastify";

type AddObservationProps = {
  observationData: any;
  open: boolean;
  onClose: () => void;
  onSave: (data?: any) => void;
  isEdit: boolean;
  clinicUniqueId: string;
  dateFilter: {
    startDate: string;
    endDate: string;
  };
};

const AddObservation: React.FC<AddObservationProps> = ({
  observationData,
  open,
  onClose,
  onSave,
  clinicUniqueId,
  dateFilter,
  isEdit,
}) => {
  const { handleSubmit, control, reset } = useForm<any>({
    defaultValues: {
      category: "",
      description: "",
      impact: "Positive",
    },
  });

  useEffect(() => {
    if (observationData && isEdit) {
      reset({
        category: observationData?.category || "",
        description: observationData?.description || "",
        impact: observationData?.impact || "Positive",
      });
    } else {
      reset({
        category: "",
        description: "",
        impact: "Positive",
      });
    }
  }, [observationData]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (data: any) => {
    console.log(data, "clinicUniqueId");
    setIsLoading(true);
    setError(null);

    try {
      // Extract month and year from endDate
      const endDate = new Date(dateFilter.endDate);
      const month = endDate.getMonth() + 1; // getMonth() returns 0-11
      const year = endDate.getFullYear();

      const api = isEdit ? updateObservation : addObservation;

      const response = await api(clinicUniqueId, {
        ...(observationData?.id && { id: observationData?.id }),
        category: data.category,
        description: data.description,
        impact: data.impact,
        month: month.toString(),
        year: year.toString(),
      });

      if (response.status.msg === "Success") {
        toast.success("Observation added successfully");
        onSave(response);
        onClose();
        reset();
      }
    } catch (err) {
      setError("Failed to save observation. Please try again.");
      console.error("Error saving observation:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="add-observation-modal-title"
      aria-describedby="add-observation-modal-description"
    >
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500, // or use maxWidth like the Dialog
          backgroundColor: "#fff",
          borderRadius: "10px",
          outline: "none", // Remove outline on focus
        }}
      >
        <div
          style={{
            backgroundColor: "#194e5b",
            color: "white",
            borderTopLeftRadius: "10px",
            borderTopRightRadius: "10px",
            padding: "16px",
            fontSize: "1.25rem",
            fontWeight: "500",
          }}
          id="add-observation-modal-title"
        >
          {isEdit ? "Edit Observation" : "Add New Observation"}
        </div>
        <div style={{ padding: "16px" }} id="add-observation-modal-description">
          <Controller
            name="category"
            control={control}
            defaultValue=""
            rules={{ required: "category is required" }}
            render={({ field, fieldState: { error } }) => (
              <CustomRankingInput
                label="Category"
                placeholder="e.g. Website"
                field={field}
                helperText="The area this observation relates to"
                fieldState={
                  {
                    error: error
                      ? { type: error.type, message: error.message }
                      : undefined,
                  } as any
                } // Explicitly cast to any
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            rules={{
              required: "description is required",
            }}
            render={({ field, fieldState: { error } }) => (
              <CustomRankingInput
                placeholder="Describe what you observed..."
                type="textarea"
                label="Description"
                helperText=""
                field={field}
                fieldState={
                  {
                    error: error
                      ? { type: error.type, message: error.message }
                      : undefined,
                  } as any
                } // Explicitly cast to any
              />
            )}
          />
          <div className="px-6" style={{ paddingLeft: 0, paddingRight: 0 }}>
            <CustomSelect
              name="impact"
              control={control}
              label="Impact"
              options={[
                { value: "Positive", label: "Positive" },
                { value: "Negative", label: "Negative" },
                { value: "Neutral", label: "Neutral" },
              ]}
              rules={{ required: "Please select a impact" }}
              className="w-full "
            />
          </div>
        </div>
        <div
          style={{
            padding: "16px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={handleClose}
            className="btn btn-sm"
            style={{
              backgroundColor: "#c9eeeb",
              color: "#0E3843",
              marginRight: "8px",
            }}
          >
            Cancel
          </button>
          <button
            disabled={isLoading}
            onClick={handleSubmit(handleSave)}
            style={{ backgroundColor: "#0E3843" }}
            className="btn btn-sm bg-[#0E3843] rounded-md text-white border hover:bg-[#0A2A30]"
          >
            {isLoading ? "Saving..." : "Save Observation"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddObservation;
